import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ImageBadge from 'components/images/ImageBadge';
import { BaseModal } from 'components/modals/BaseModal';
import { Color } from 'core';
import { useGetSetActivityMetrics } from 'hooks/useGetSetActivityMetrics';
import moment from 'moment';
import React from 'react';
import styled from 'styled-components';

interface IProps {
  contentItem: any;
  onClose: () => void;
}

export const ContentUsageModal = (props: IProps) => {
  const { contentItem, onClose } = props;
  const courses = contentItem.courses || [];
  const { data: metrics, isLoading: metricsLoading } = useGetSetActivityMetrics(contentItem.id);

  const getTypeDisplay = (goalType: string) => {
    const typeMap: { [key: string]: string } = {
      set: 'Learning Set',
      survey: 'Survey',
      assessment: 'Assessment',
      scorm: 'SCORM',
    };
    return typeMap[goalType] || goalType;
  };

  const getCourseCountText = (courses: any[]) => {
    const count = courses?.length || 0;
    if (count === 0) return 'No courses';
    if (count === 1) return '1 course';
    return `${count} courses`;
  };

  return (
    <BaseModal title="Content Usage" onClose={onClose}>
      <WideContent>
        <ContentItemSection>
          <SectionTitle>Content Item</SectionTitle>
          <ContentItemRow>
            <ImageBadge
              image={contentItem.image}
              style={{ width: '40px', height: '40px', marginRight: '16px' }}
              entity="set"
            />
            <ContentItemDetails>
              <ContentItemName>{contentItem.name}</ContentItemName>
              <ContentItemType>{getTypeDisplay(contentItem.goalType)}</ContentItemType>
            </ContentItemDetails>
          </ContentItemRow>
        </ContentItemSection>

        <MetricsSection>
          <SectionTitle>Activity Metrics</SectionTitle>
          {metricsLoading ? (
            <MetricsLoading>Loading metrics...</MetricsLoading>
          ) : (
            <MetricsGrid>
              <MetricItem>
                <MetricLabel>Used In</MetricLabel>
                <MetricValue>{getCourseCountText(courses)}</MetricValue>
              </MetricItem>
              <MetricItem>
                <MetricLabel>Total Learners</MetricLabel>
                <MetricValue>{metrics?.totalLearnerCount ?? '-'}</MetricValue>
              </MetricItem>
              <MetricItem>
                <MetricLabel>Last Studied</MetricLabel>
                <MetricValue>
                  {metrics?.lastStudiedAt ? moment(metrics.lastStudiedAt).format('MM/DD/YY') : 'Not yet studied'}
                </MetricValue>
              </MetricItem>
              <MetricItem>
                <MetricLabel>Course Last Studied In</MetricLabel>
                <MetricValue>
                  {/* It's possible that last studied has a date but there is no course because the person was removed from the course */}
                  {courses.length === 1 && !!metrics?.lastStudiedAt
                    ? courses[0].name
                    : metrics?.mostRecentlyActiveCourse?.name ?? 'None'}
                </MetricValue>
              </MetricItem>
            </MetricsGrid>
          )}
        </MetricsSection>

        {courses.length > 0 && (
          <TableSection>
            <Table>
              <TableHeader>
                <tr>
                  <th>Course Name</th>
                  <th>Actions</th>
                </tr>
              </TableHeader>
              <TableBody>
                {courses.map((course: any) => (
                  <tr key={course.id}>
                    <td>{course.name}</td>
                    <td>
                      <ViewCourseLink onClick={() => window.open(`/app/nav/courses/${course.id}`, '_blank')}>
                        View Course <FontAwesomeIcon icon={faExternalLinkAlt} />
                      </ViewCourseLink>
                    </td>
                  </tr>
                ))}
              </TableBody>
            </Table>
          </TableSection>
        )}
      </WideContent>
    </BaseModal>
  );
};

const WideContent = styled.div`
  min-width: 50vw;
`;

const ContentItemSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 500;
  color: ${Color.darkGray};
`;

const ContentItemRow = styled.div`
  display: flex;
  align-items: center;
`;

const ContentItemDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const ContentItemName = styled.div`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const ContentItemType = styled.div`
  font-size: 14px;
  color: ${Color.darkGray};
`;

const TableSection = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  th {
    text-align: left;
    padding: 12px;
    font-size: 12px;
    font-weight: 500;
    color: ${Color.darkGray};
    text-transform: uppercase;
    border-bottom: 1px solid ${Color.lightGray};
  }
`;

const TableBody = styled.tbody`
  td {
    padding: 16px 12px;
    border-bottom: 1px solid ${Color.veryLightGray};
    font-size: 14px;
  }
`;

const ViewCourseLink = styled.button`
  background: none;
  border: none;
  color: ${Color.primaryBlue};
  cursor: pointer;
  text-decoration: none;
  font-size: inherit;
  font-family: inherit;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    text-decoration: underline;
  }
`;

const MetricsSection = styled.div`
  margin-bottom: 24px;
`;

const MetricsLoading = styled.div`
  color: ${Color.darkGray};
  font-style: italic;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

const MetricItem = styled.div`
  background: ${Color.veryLightGray};
  padding: 12px 16px;
  border-radius: 8px;
`;

const MetricLabel = styled.div`
  font-size: 12px;
  color: ${Color.darkGray};
  margin-bottom: 4px;
`;

const MetricValue = styled.div`
  font-size: 16px;
  font-weight: 600;
`;
